import React from 'react';
import { TrendingUp, CheckCircle, Zap, Target, ArrowRight, Clock, IndianRupee, Briefcase, Lightbulb, Phone, Mail } from 'lucide-react';

const pageStyle = {
  width: '210mm',
  height: '297mm',
  padding: '15mm',
  fontFamily: 'Inter, system-ui, sans-serif',
  color: '#1f2937',
  fontSize: '11pt',
  lineHeight: '1.5',
  boxSizing: 'border-box',
  overflow: 'hidden',
  position: 'relative'
};

const AuditReportPrint = ({ data, onReady }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (onReady) onReady();
    }, 500);
    return () => clearTimeout(timer);
  }, [onReady]);

  if (!data) return null;

  // Handle "Other" industry display
  const displayIndustry = (data.industry === "Other" || data.industry === "Your Business")
    ? "Your Business"
    : data.industry;
  const isCustomIndustry = data.isCustomIndustry || data.industry === "Other" || data.industry === "Your Business";

  return (
    <div id="audit-report-print">
      {/* PAGE 1 */}
      <div className="audit-page bg-white" style={pageStyle}>
        {/* Header */}
        <div className="flex justify-between items-start mb-5 pb-3 border-b-2 border-blue-800">
          <div>
            <img src="/automation1st-logo.png" alt="AutomationFirst" style={{ height: '26px' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
            <h1 className="text-xl font-bold text-blue-800" style={{ display: 'none' }}>AutomationFirst</h1>
            <p className="text-xs text-gray-500 mt-1">Making easy work of workflows</p>
          </div>
          <div className="text-right text-xs text-gray-500">
            <p>hello@automation1st.com</p>
            <p>+91 9324280636</p>
            <p>automation1st.com</p>
          </div>
        </div>

        {/* Title */}
        <div className="mb-5">
          <h2 className="text-2xl font-bold text-blue-800 mb-1">
            Automation Audit Report
          </h2>
          <p className="text-gray-600 text-sm">
            Prepared exclusively for <span className="font-semibold text-gray-800">{data.company}</span>
          </p>
        </div>

        {/* Info Bar */}
        <div className="bg-gray-50 rounded-lg p-3 mb-5 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500 text-xs">Prepared For</p>
            <p className="font-semibold text-sm">{data.contactName}</p>
            <p className="text-gray-600 text-xs">{data.email}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">Business Details</p>
            <p className="font-semibold text-sm">{data.company}</p>
            <p className="text-gray-600 text-xs">{displayIndustry} | {data.teamSize}</p>
          </div>
        </div>

        {/* Score Hero */}
        <div
          className="rounded-xl p-6 mb-5 text-center"
          style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}
        >
          <p className="text-blue-100 uppercase tracking-wider text-xs mb-2">
            Your Automation Opportunity Score
          </p>
          <div className="flex items-center justify-center gap-2">
            <span className="text-5xl font-bold text-orange-400">{data.automationScore}</span>
            <span className="text-2xl text-white/80">/100</span>
          </div>
          <p className="text-blue-100 mt-2 text-xs">
            {data.automationScore >= 80
              ? "Excellent potential for automation"
              : data.automationScore >= 70
              ? "Strong automation opportunities identified"
              : "Good automation opportunities identified"}
          </p>
        </div>

        {/* Savings Cards */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-emerald-600" />
              <span className="text-emerald-700 text-xs font-medium">Time Savings Potential</span>
            </div>
            <p className="text-xl font-bold text-emerald-800">{data.timeSavingsPotential}</p>
            <p className="text-emerald-600 text-xs mt-1">Hours your team could reclaim weekly</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <IndianRupee className="w-4 h-4 text-orange-600" />
              <span className="text-orange-700 text-xs font-medium">Cost Savings Potential</span>
            </div>
            <p className="text-xl font-bold text-orange-800">{data.costReductionPotential}</p>
            <p className="text-orange-600 text-xs mt-1">Estimated monthly savings</p>
          </div>
        </div>

        {/* Quick Win */}
        {data.departmentQuickWin && (
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-3 mb-5">
            <div className="flex items-start gap-2">
              <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-800 text-sm">Quick Win for {data.department}</p>
                <p className="text-blue-700 text-xs mt-1">{data.departmentQuickWin}</p>
                <p className="text-blue-600 text-xs mt-0.5">{data.departmentImpact}</p>
              </div>
            </div>
          </div>
        )}

        {/* Industry Insight */}
        {data.industryBenchmark && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-5">
            <div className="flex items-start gap-2">
              <TrendingUp className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-orange-800 text-sm">
                  {isCustomIndustry ? 'How Businesses Like Yours Win' : `How ${displayIndustry} Businesses Win`}
                </p>
                <p className="text-orange-700 text-xs mt-1 italic">
                  "{data.industryBenchmark.caseStudy}"
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Key Insights */}
        {data.keyInsights && data.keyInsights.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-bold text-gray-800">Key Insights</h3>
            </div>
            <div className="space-y-1.5">
              {data.keyInsights.map((insight, index) => (
                <div key={index} className="flex items-start gap-2 bg-gray-50 rounded-lg p-2">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-xs">{insight}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Page 1 Footer */}
        <div style={{ position: 'absolute', bottom: '15mm', left: '15mm', right: '15mm' }}>
          <div className="pt-3 border-t border-gray-200 text-center text-xs text-gray-400">
            <p>AutomationFirst | Making easy work of workflows | Page 1 of 2</p>
          </div>
        </div>
      </div>

      {/* PAGE 2 */}
      <div className="audit-page bg-white" style={pageStyle}>
        {/* Mini Header */}
        <div className="flex justify-between items-center mb-6 pb-2 border-b border-gray-200">
          <img src="/automation1st-logo.png" alt="AutomationFirst" style={{ height: '20px' }} onError={(e) => { e.target.style.display = 'none'; }} />
          <p className="text-xs text-gray-400">Automation Audit Report - {data.company}</p>
        </div>

        {/* Top Priorities */}
        {data.priorityAreas && data.priorityAreas.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5 text-blue-600" />
              <h3 className="text-base font-bold text-gray-800">Your Top Automation Priorities</h3>
            </div>
            <div className="space-y-2">
              {data.priorityAreas.map((area, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-800 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-gray-800 text-sm font-medium">{area}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tailored Solutions */}
        {data.industrySpecificSolutions && data.industrySpecificSolutions.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="w-5 h-5 text-orange-600" />
              <h3 className="text-base font-bold text-gray-800">
                {isCustomIndustry ? 'Tailored Solutions for Your Business' : `Tailored for ${displayIndustry}`}
              </h3>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <ul className="space-y-2">
                {data.industrySpecificSolutions.map((solution, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0 mt-2"></div>
                    <p className="text-gray-700 text-sm">{solution}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Recommended Next Steps */}
        {data.recommendedNextSteps && data.recommendedNextSteps.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <ArrowRight className="w-5 h-5 text-blue-600" />
              <h3 className="text-base font-bold text-gray-800">Recommended Next Steps</h3>
            </div>
            <div className="space-y-2.5">
              {data.recommendedNextSteps.map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center font-semibold text-xs flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 text-sm pt-0.5">{step}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Box */}
        <div
          className="rounded-xl p-5 text-center mt-6"
          style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}
        >
          <h3 className="text-lg font-bold text-white mb-2">Ready to Get Started?</h3>
          <p className="text-blue-100 text-sm mb-3">
            Book a free 30-minute strategy call to create your personalised automation roadmap.
          </p>
          <div className="flex justify-center gap-6 text-white">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">+91 9324280636</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span className="text-sm font-medium">hello@automation1st.com</span>
            </div>
          </div>
        </div>

        {/* Page 2 Footer */}
        <div style={{ position: 'absolute', bottom: '15mm', left: '15mm', right: '15mm' }}>
          <div className="pt-3 border-t border-gray-200 text-center text-xs text-gray-400">
            <p>AutomationFirst | Making easy work of workflows | Page 2 of 2</p>
            <p className="mt-0.5">Report generated: {data.date} | Confidential - Prepared for {data.company}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditReportPrint;
